import Cookies from 'js-cookie'
import { isEmpty } from 'lodash-es'
import { StatusCodes } from 'http-status-codes'
import { defineStore } from 'pinia'

import router from '@/router'
import { paths } from '@/router/paths'
import commonAPIS from '@/api/common'
import { request } from '@/utils/generateAPIs'
import { getCookie } from '@/utils/common'
import { LOCALE_KEYS } from '@/locale'
import { useAccessesStore } from './useAccesses'
import { LOCALSTORAGE_KEYS, INFO_ENUM, RoleTypeEnum } from '@/static/enums'

export interface RoleDepartmentDTO {
  roleId: string
  roleName: string
  departmentId?: any
  departmentName?: any
  rootFolderId?: any
  departmentAttributesId?: any
  departmentAttributesName?: any
}

export interface RoleMenuPermissionDTO {
  id: string
  permissionName: string
  parentId?: any
  code: string
  permissionTree?: any
}

// 0 经销商 1 内部
export enum UserType {
  DEALER = '0',
  INTERNAL = '1',
}

export type UserInfo = {
  id: string
  userCode: string
  userNameCn?: any
  userNameEn: string
  email?: any
  type: UserType
  dealerCode?: any
  regionNameCn?: any
  regionNameEn?: any
  dealerShortNameCn?: any
  dealerShortNameEn?: any
  positionNameCn?: any
  positionNameEn?: any
  department: string
  dealerNameCn?: any
  dealerNameEn?: any
  dealerGroupCn?: any
  dealerGroupEn?: any
  roles: string[]
  roleDepartmentDTOS: RoleDepartmentDTO[]
  roleMenuPermissionDTOS: RoleMenuPermissionDTO[]
  firstLogin: number
}

export type MarqueeItem = {
  id: string
  title: string
  contentHtml: string
  displayOrder: number
  startTime: string
  endTime: string
  updateTime: string
}

export const useCommonStore = defineStore('COMMON_STORE', {
  state: () => ({
    role: localStorage.getItem(LOCALSTORAGE_KEYS.ROLE) ?? '',
    locale: (localStorage.getItem(LOCALSTORAGE_KEYS.LOCALE) as LOCALE_KEYS) || LOCALE_KEYS.CN,
    system: '',
    userInfo: {} as UserInfo,
    marqueeInfo: [] as MarqueeItem[],
    // persisted
    isCollapse: false,
    isListUpload: false,
  }),
  actions: {
    async setToken(query: any) {
      const tokenInCookie = getCookie('token')
      const token = isEmpty(tokenInCookie) ? query.token : tokenInCookie

      if (!isEmpty(token)) {
        Cookies.remove('token')
        Cookies.remove('departmentRoleInfo')

        if (Number(token) === StatusCodes.UNAUTHORIZED) {
          return router.replace(paths.home)
        }

        localStorage.setItem(LOCALSTORAGE_KEYS.TOKEN, token)

        try {
          const redirect = await this.getCurrentUserInfo()
          return router.replace(redirect || paths.root)
        } catch (error) {
          console.log('setToken =>', error)
        }
      }
    },
    async logout() {
      try {
        await request({ url: '/api/openid/logout', method: 'GET' })
        localStorage.removeItem(LOCALSTORAGE_KEYS.ORG)
        localStorage.removeItem(LOCALSTORAGE_KEYS.ROLE)
        localStorage.removeItem(LOCALSTORAGE_KEYS.TOKEN)
        localStorage.removeItem(LOCALSTORAGE_KEYS.REDIRECT)
        localStorage.removeItem(LOCALSTORAGE_KEYS.ROLE)
        localStorage.removeItem(INFO_ENUM.DEPARTMENT_ID)
        localStorage.removeItem(INFO_ENUM.ROLE_ID)

        sessionStorage.removeItem('ACCESSES')
        sessionStorage.removeItem('COMMON_STORE')

        Cookies.remove('token')
      } catch (err) {
        console.log('exitLogin ===>', err)
      }
    },
    async getCurrentUserInfo() {
      try {
        const { code, data } = await commonAPIS.getUserInfo()
        if (code === StatusCodes.OK) {
          this.userInfo = data

          if (data.roles.length === 1) {
            localStorage.setItem(LOCALSTORAGE_KEYS.ROLE, data.roles[0])
            this.role = data.roles[0] as RoleTypeEnum
          }

          if (this.role) {
            const accesses = useAccessesStore()
            accesses.setAccesses([this.role as RoleTypeEnum])
          } else {
            localStorage.removeItem(LOCALSTORAGE_KEYS.ROLE)
            return paths.departmentRole
          }

          if (data.roles.length === 1 && data.firstLogin === 0) {
            localStorage.setItem(INFO_ENUM.DEPARTMENT_ID, data.roleDepartmentDTOS[0].departmentId)

            const shareUrl = localStorage.getItem(LOCALSTORAGE_KEYS.REDIRECT)
            if (shareUrl && shareUrl.indexOf('/#/share/') !== -1) {
              localStorage.removeItem(LOCALSTORAGE_KEYS.REDIRECT)
              return shareUrl.split('/#')[1]
            }
          }
        }
      } catch (error) {
        console.log('getCurrentUserInfo =>', error)
        localStorage.setItem(LOCALSTORAGE_KEYS.REDIRECT, window.location.href)

        const token = localStorage.getItem(LOCALSTORAGE_KEYS.TOKEN)
        if (Number(token) === StatusCodes.UNAUTHORIZED) router.replace(paths.home)

        throw error
      }
    },
    async getActiveMarquee() {
      try {
        const res = await commonAPIS.getActiveMarquee()
        this.marqueeInfo = res?.data || []
      } catch (error) {
        console.log('getActiveMarquee error', error)
      }
    },
    setLocale(data: LOCALE_KEYS) {
      this.locale = data
      localStorage.setItem(LOCALSTORAGE_KEYS.LOCALE, data)
    },
  },
  getters: {
    isProductionMode() {
      return import.meta.env.MODE === 'prod'
    },
  },
  persistedState: {
    storage: window.localStorage,
    includePaths: ['isCollapse'],
  },
})
